import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Paperclip,
  Plus,
  Scale,
  Trash2,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { confirmDialog } from '../lib/dialogs'
import { todayAsInputDate } from '../lib/datetime'
import { currentLocaleTag } from '../lib/formatting'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../lib/money'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import type {
  JournalAttachmentDto,
  JournalEntryDto,
  JournalEntryListResponse,
  JournalEntryStatusT,
} from '@toga/shared'

const STATUS_PILL: Record<JournalEntryStatusT, string> = {
  BORRADOR: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  ASENTADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  ANULADO: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40',
}

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

export default function AccountingJournal() {
  const { t } = useTranslation(['accounting', 'common'])
  const currentYear = new Date().getFullYear()

  const [year, setYear] = useState<string>('')
  const [status, setStatus] = useState<'' | JournalEntryStatusT>('')
  const [accountCode, setAccountCode] = useState('')
  // F1-G — alta manual de asientos desde la web (la API ya existía).
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 50

  const [data, setData] = useState<JournalEntryListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (status) params.set('status', status)
      if (accountCode.trim()) params.set('accountCode', accountCode.trim())
      params.set('page', String(page))
      params.set('pageSize', String(pageSize))
      setData(await api.get<JournalEntryListResponse>(`/accounting/journal?${params.toString()}`))
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('journal.errorLoad')
      setError(text)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [year, status, accountCode, page, t])

  useEffect(() => {
    void reload()
  }, [reload])

  // Al cambiar filtros volvemos a la página 1.
  useEffect(() => {
    setPage(1)
  }, [year, status, accountCode])

  const rows = useMemo(
    () =>
      (data?.items ?? []).map((e) => ({
        ...e,
        when: new Date(e.entryDate).toLocaleDateString(currentLocaleTag()),
      })),
    [data],
  )
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <BookOpen size={20} className="text-indigo-600 dark:text-indigo-300" />
            {t('journal.title')}
          </h1>
          <p className="text-xs t-muted">{t('journal.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm"
        >
          <Plus size={15} /> {t('journal.newEntry')}
        </button>
      </header>

      {creating && (
        <JournalEntryEditor
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void reload()
          }}
        />
      )}

      <section
        className="rounded-xl border p-4 grid grid-cols-1 md:grid-cols-4 gap-3"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
      >
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('journal.filters.year')}
          <select value={year} onChange={(e) => setYear(e.target.value)} className={inputCls} style={inputStyle}>
            <option value="">{t('journal.filters.allYears')}</option>
            {Array.from({ length: 7 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs t-muted">
          {t('journal.filters.status')}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | JournalEntryStatusT)}
            className={inputCls}
            style={inputStyle}
          >
            <option value="">{t('journal.filters.allStatuses')}</option>
            <option value="BORRADOR">{t('enums:JournalEntryStatus.BORRADOR')}</option>
            <option value="ASENTADO">{t('enums:JournalEntryStatus.ASENTADO')}</option>
            <option value="ANULADO">{t('enums:JournalEntryStatus.ANULADO')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs t-muted md:col-span-2">
          {t('journal.filters.accountCode')}
          <input
            type="text"
            value={accountCode}
            onChange={(e) => setAccountCode(e.target.value)}
            placeholder={t('journal.filters.accountCodePlaceholder')}
            className={inputCls}
            style={inputStyle}
          />
        </label>
      </section>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <section className="rounded-xl border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}>
        <div
          className="flex items-center justify-between px-4 py-2 text-xs t-muted"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <span>
            {loading ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 size={12} className="animate-spin" />
                {t('journal.loading')}
              </span>
            ) : (
              t('journal.recordsCount', { count: total })
            )}
          </span>
          {totalPages > 1 && (
            <span className="inline-flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="p-1 rounded hover:bg-hover disabled:opacity-40"
                aria-label={t('common:actions.previous')}
              >
                <ChevronLeft size={14} />
              </button>
              {t('journal.pageOf', { page, totalPages })}
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="p-1 rounded hover:bg-hover disabled:opacity-40"
                aria-label={t('common:actions.next')}
              >
                <ChevronRight size={14} />
              </button>
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs t-muted uppercase tracking-wider"
                style={{ borderBottom: '1px solid var(--border)' }}
              >
                <th className="px-4 py-2">{t('journal.columns.number')}</th>
                <th className="px-4 py-2">{t('journal.columns.date')}</th>
                <th className="px-4 py-2">{t('journal.columns.description')}</th>
                <th className="px-4 py-2">{t('journal.columns.source')}</th>
                <th className="px-4 py-2">{t('journal.columns.status')}</th>
                <th className="px-4 py-2 text-right">{t('journal.columns.debit')}</th>
                <th className="px-4 py-2 text-right">{t('journal.columns.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-xs t-muted">
                    {t('journal.empty')}
                  </td>
                </tr>
              ) : (
                rows.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setDetailId(e.id)}
                    style={{ borderBottom: '1px solid var(--border)' }}
                    className="t-primary cursor-pointer hover:bg-hover"
                  >
                    <td className="px-4 py-1.5 font-mono text-xs whitespace-nowrap">
                      {e.year}/{e.number}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-xs">{e.when}</td>
                    <td className="px-4 py-1.5 max-w-md truncate">
                      <span className="inline-flex items-center gap-1.5">
                        {e.description}
                        {e.attachmentCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] t-muted shrink-0"
                            title={t('journal.attachments.count', { count: e.attachmentCount })}
                          >
                            <Paperclip size={11} />
                            {e.attachmentCount}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 font-mono text-[11px] t-muted">{e.source}</td>
                    <td className="px-4 py-1.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_PILL[e.status]}`}
                      >
                        {t('enums:JournalEntryStatus.' + e.status)}
                      </span>
                    </td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs">{formatCents(e.totalDebitCents)}</td>
                    <td className="px-4 py-1.5 text-right font-mono text-xs">{formatCents(e.totalCreditCents)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {detailId && (
        <JournalEntryDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onChanged={() => void reload()}
        />
      )}
    </div>
  )
}

function JournalEntryDetailModal({
  id,
  onClose,
  onChanged,
}: {
  id: string
  onClose: () => void
  onChanged?: () => void
}) {
  const { t } = useTranslation(['accounting', 'common'])
  const [entry, setEntry] = useState<JournalEntryDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ entry: JournalEntryDto }>(`/accounting/journal/${id}`)
      setEntry(r.entry)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('journal.errorLoad'))
    }
  }, [id, t])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <Modal open onClose={onClose} title={t('journal.detail.title')} maxWidth="max-w-2xl">
      {!entry && !error && (
        <div className="t-faint text-sm flex items-center gap-1.5 py-6 justify-center">
          <Loader2 size={14} className="animate-spin" /> {t('journal.loading')}
        </div>
      )}
      {error && (
        <div className="text-sm text-rose-700 dark:text-rose-300 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {entry && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Meta label={t('journal.columns.number')} value={`${entry.year}/${entry.number}`} mono />
            <Meta label={t('journal.columns.date')} value={new Date(entry.entryDate).toLocaleDateString(currentLocaleTag())} />
            <Meta label={t('journal.columns.status')} value={t('enums:JournalEntryStatus.' + entry.status)} />
            <Meta label={t('journal.columns.source')} value={entry.source} mono />
          </div>
          <div>
            <div className="text-xs t-muted uppercase tracking-wider mb-1">{t('journal.columns.description')}</div>
            <div className="t-primary text-sm">{entry.description}</div>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
                <th className="py-1.5">{t('journal.detail.account')}</th>
                <th className="py-1.5">{t('journal.columns.description')}</th>
                <th className="py-1.5 text-right">{t('journal.columns.debit')}</th>
                <th className="py-1.5 text-right">{t('journal.columns.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {(entry.lines ?? []).map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                  <td className="py-1.5 font-mono text-xs">
                    {l.accountCode}
                    {l.accountName && <span className="t-muted"> · {l.accountName}</span>}
                  </td>
                  <td className="py-1.5 text-xs t-secondary">{l.description ?? '—'}</td>
                  <td className="py-1.5 text-right font-mono text-xs">{l.debitCents ? formatCents(l.debitCents) : ''}</td>
                  <td className="py-1.5 text-right font-mono text-xs">{l.creditCents ? formatCents(l.creditCents) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold t-primary">
                <td className="py-1.5" colSpan={2}>
                  {t('journal.detail.totals')}
                </td>
                <td className="py-1.5 text-right font-mono text-xs">{formatCents(entry.totalDebitCents)}</td>
                <td className="py-1.5 text-right font-mono text-xs">{formatCents(entry.totalCreditCents)}</td>
              </tr>
            </tfoot>
          </table>

          <AttachmentsPanel
            entryId={entry.id}
            items={entry.attachments ?? []}
            onChanged={async () => {
              await load()
              onChanged?.()
            }}
          />
        </div>
      )}
    </Modal>
  )
}

// ============================================================================
// F1-H — Justificantes del asiento (paridad Ledgeria).
//
// Los asientos automáticos ya tienen su papel por otra vía (el gasto lleva su
// factura escaneada). Esto da respaldo documental a los MANUALES: nóminas,
// liquidaciones de impuestos, regularizaciones, aportaciones de socio.
// ============================================================================

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AttachmentsPanel({
  entryId,
  items,
  onChanged,
}: {
  entryId: string
  items: JournalAttachmentDto[]
  onChanged: () => Promise<void> | void
}) {
  const { t } = useTranslation(['accounting', 'common'])
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  // El backend solo sirve los justificantes a contabilidad; para el resto ni
  // siquiera pintamos el panel, que vacío daría a entender que no hay ninguno.
  const canSee = role === 'ADMIN' || role === 'CONTABILIDAD'
  const canWrite = role === 'ADMIN'
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const failureText = (err: unknown, fallback: string) =>
    err instanceof ApiError
      ? ((err.body as { error?: string } | undefined)?.error ?? err.message)
      : err instanceof Error
        ? err.message
        : fallback

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      await api.upload(`/accounting/journal/${entryId}/attachments`, form)
      await onChanged()
    } catch (err) {
      setError(failureText(err, t('journal.attachments.errorUpload')))
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const onDelete = async (att: JournalAttachmentDto) => {
    if (
      !(await confirmDialog({
        message: t('journal.attachments.confirmDelete', { name: att.fileName }),
        tone: 'danger',
      }))
    )
      return
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/accounting/journal/${entryId}/attachments/${att.id}`)
      await onChanged()
    } catch (err) {
      setError(failureText(err, t('journal.attachments.errorDelete')))
    } finally {
      setBusy(false)
    }
  }

  if (!canSee) return null

  return (
    <div className="pt-2" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-xs t-muted uppercase tracking-wider flex items-center gap-1.5">
          <Paperclip size={12} /> {t('journal.attachments.title')}
        </h3>
        {canWrite && (
          <label className="text-xs text-toga-700 dark:text-toga-300 hover:underline cursor-pointer inline-flex items-center gap-1">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {t('journal.attachments.upload')}
            <input ref={inputRef} type="file" className="hidden" onChange={onPick} disabled={busy} />
          </label>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5 mb-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs t-faint">{t('journal.attachments.empty')}</p>
      ) : (
        <ul className="space-y-1">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 text-xs t-secondary rounded px-2 py-1 hover:bg-hover"
            >
              <Paperclip size={11} className="t-faint shrink-0" />
              <span className="truncate t-primary flex-1" title={a.fileName}>
                {a.fileName}
              </span>
              <span className="t-faint shrink-0 font-mono text-[10px]">{formatBytes(a.size)}</span>
              {a.uploadedByName && (
                <span className="t-faint shrink-0 hidden md:inline">{a.uploadedByName}</span>
              )}
              <a
                href={`/api/accounting/journal/${entryId}/attachments/${a.id}/download`}
                target="_blank"
                rel="noreferrer"
                className="p-1 rounded hover:bg-hover shrink-0"
                title={t('common:actions.download')}
              >
                <Download size={12} />
              </a>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => void onDelete(a)}
                  disabled={busy}
                  className="p-1 rounded hover:bg-hover text-rose-600 dark:text-rose-400 disabled:opacity-40 shrink-0"
                  title={t('common:actions.delete')}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs t-muted uppercase tracking-wider mb-0.5">{label}</div>
      <div className={`t-primary ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</div>
    </div>
  )
}

// ============================================================================
// F1-G — Editor de asientos manuales (paridad Ledgeria).
// ============================================================================

interface DraftLine {
  key: string
  accountCode: string
  description: string
  debitEuros: string
  creditEuros: string
}

function emptyLine(): DraftLine {
  return { key: crypto.randomUUID(), accountCode: '', description: '', debitEuros: '', creditEuros: '' }
}

/**
 * Alta manual de asientos. La API ya existía (`POST /accounting/journal`), pero
 * el Diario era de solo lectura: no había forma de meter un asiento a mano
 * —una amortización, una reclasificación, un ajuste de la gestoría— sin entrar
 * en la base de datos.
 *
 * El botón CUADRAR es el gesto de ContaPlus: calcula el descuadre y lo coloca
 * en el lado que falta, para no tener que hacer la resta a mano.
 */
function JournalEntryEditor({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation(['accounting', 'common'])
  // Hoy en la zona del usuario. `toISOString()` da el día UTC, así que de
  // madrugada proponía AYER — y de esta fecha salen el ejercicio y el número de
  // asiento. La noche del 31 de diciembre eso lo metía en el año ya cerrado.
  const today = todayAsInputDate()

  const [entryDate, setEntryDate] = useState(today)
  const [description, setDescription] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()])
  const [accounts, setAccounts] = useState<Array<{ code: string; name: string }>>([])
  const [saving, setSaving] = useState<'draft' | 'posted' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Catálogo de cuentas para el desplegable (reutiliza el buscador del Mayor).
  useEffect(() => {
    api
      .get<{ items: Array<{ code: string; name: string }> }>('/accounting/ledger/accounts')
      .then((r) => setAccounts(r.items))
      .catch(() => setAccounts([]))
  }, [])

  const totals = useMemo(() => {
    const debit = lines.reduce((s, l) => s + (l.debitEuros.trim() ? parseEurosToCents(l.debitEuros) : 0), 0)
    const credit = lines.reduce((s, l) => s + (l.creditEuros.trim() ? parseEurosToCents(l.creditEuros) : 0), 0)
    return { debit, credit, diff: debit - credit }
  }, [lines])

  const patch = (key: string, p: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)))

  /** Coloca el descuadre en el lado que falta, en la primera línea libre. */
  const balance = () => {
    if (totals.diff === 0) return
    const amount = centsToEurosInput(Math.abs(totals.diff))
    // Si sobra DEBE, el hueco va al HABER, y al revés.
    const field = totals.diff > 0 ? 'creditEuros' : 'debitEuros'
    setLines((ls) => {
      const idx = ls.findIndex((l) => !l.debitEuros.trim() && !l.creditEuros.trim())
      if (idx >= 0) {
        const next = [...ls]
        next[idx] = { ...next[idx]!, [field]: amount }
        return next
      }
      return [...ls, { ...emptyLine(), [field]: amount }]
    })
  }

  const usableLines = lines.filter(
    (l) => l.accountCode.trim() && (l.debitEuros.trim() || l.creditEuros.trim())
  )
  const canSave =
    usableLines.length >= 2 &&
    description.trim().length > 0 &&
    totals.diff === 0 &&
    totals.debit > 0 &&
    saving === null

  const save = async (status: 'BORRADOR' | 'ASENTADO') => {
    setSaving(status === 'BORRADOR' ? 'draft' : 'posted')
    setError(null)
    try {
      await api.post('/accounting/journal', {
        entryDate: new Date(`${entryDate}T12:00:00.000Z`).toISOString(),
        description: description.trim(),
        status,
        lines: usableLines.map((l) => ({
          accountCode: l.accountCode.trim(),
          description: l.description.trim() || null,
          debitCents: l.debitEuros.trim() ? parseEurosToCents(l.debitEuros) : 0,
          creditCents: l.creditEuros.trim() ? parseEurosToCents(l.creditEuros) : 0,
        })),
      })
      onCreated()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('journal.editor.saveError')))
    } finally {
      setSaving(null)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('journal.editor.title')} maxWidth="max-w-4xl">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1 text-xs t-muted">
            {t('journal.editor.date')}
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className={inputCls}
              style={inputStyle}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs t-muted md:col-span-2">
            {t('journal.editor.description')}
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('journal.editor.descriptionPlaceholder')}
              className={inputCls}
              style={inputStyle}
            />
          </label>
        </div>

        <datalist id="journal-accounts">
          {accounts.map((a) => (
            <option key={a.code} value={a.code}>
              {a.name}
            </option>
          ))}
        </datalist>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider t-faint">
                <th className="py-1 w-36">{t('table.account')}</th>
                <th className="py-1">{t('table.description')}</th>
                <th className="py-1 w-32 text-right">{t('table.debit')}</th>
                <th className="py-1 w-32 text-right">{t('table.credit')}</th>
                <th className="py-1 w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key} className="border-t border-base/40">
                  <td className="py-1 pr-2">
                    <input
                      list="journal-accounts"
                      value={l.accountCode}
                      onChange={(e) => patch(l.key, { accountCode: e.target.value })}
                      placeholder="572"
                      className={`${inputCls} w-full font-mono`}
                      style={inputStyle}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      value={l.description}
                      onChange={(e) => patch(l.key, { description: e.target.value })}
                      className={`${inputCls} w-full`}
                      style={inputStyle}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      value={l.debitEuros}
                      onChange={(e) => patch(l.key, { debitEuros: e.target.value, creditEuros: '' })}
                      placeholder="0,00"
                      inputMode="decimal"
                      className={`${inputCls} w-full text-right font-mono`}
                      style={inputStyle}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      value={l.creditEuros}
                      onChange={(e) => patch(l.key, { creditEuros: e.target.value, debitEuros: '' })}
                      placeholder="0,00"
                      inputMode="decimal"
                      className={`${inputCls} w-full text-right font-mono`}
                      style={inputStyle}
                    />
                  </td>
                  <td className="py-1 text-right">
                    {lines.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                        className="t-faint hover:text-rose-600 dark:hover:text-rose-300"
                        title={t('journal.editor.removeLine')}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-base font-semibold">
                <td className="pt-2" colSpan={2}>
                  {t('table.totals')}
                </td>
                <td className="pt-2 text-right font-mono">{formatCents(totals.debit)}</td>
                <td className="pt-2 text-right font-mono">{formatCents(totals.credit)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setLines((ls) => [...ls, emptyLine()])}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary"
            >
              <Plus size={13} /> {t('journal.editor.addLine')}
            </button>
            <button
              type="button"
              onClick={balance}
              disabled={totals.diff === 0}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary disabled:opacity-40"
              title={t('journal.editor.balanceHint')}
            >
              <Scale size={13} /> {t('journal.editor.balance')}
            </button>
          </div>
          <span
            className={`text-xs font-mono ${
              totals.diff === 0
                ? 'text-emerald-700 dark:text-emerald-300'
                : 'text-amber-700 dark:text-amber-300'
            }`}
          >
            {totals.diff === 0
              ? t('journal.editor.balanced')
              : t('journal.editor.outOfBalance', { diff: formatCents(Math.abs(totals.diff)) })}
          </span>
        </div>

        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-200 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-base hover:bg-hover text-sm t-secondary"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => void save('BORRADOR')}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-base hover:bg-hover text-sm t-secondary disabled:opacity-40"
          >
            {saving === 'draft' && <Loader2 size={14} className="animate-spin" />}
            {t('journal.editor.saveDraft')}
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => void save('ASENTADO')}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm disabled:opacity-40"
          >
            {saving === 'posted' && <Loader2 size={14} className="animate-spin" />}
            {t('journal.editor.post')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
